import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useRef,
} from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.min.css";
import "ag-grid-community/dist/styles/ag-theme-balham.min.css";
import "./AgGrid.css";
import { focusCell } from "../utils/gridUtils";
const Aggrid = forwardRef((props, ref) => {
  const {
    id,
    rowData,
    columnDefs,
    defaultColDef,
    frameworkComponents,
    quickFilterText,
    rowSelection = "single",
    singleClickEdit = true,
    stopEditingWhenCellsLoseFocus = false,
    suppressHorizontalScroll = true,
    suppressCellSelection = true,
    animateRows = true,
    isFullWidthCell,
    fullWidthCellRenderer,
    fullWidthCellRendererFramework,
    fullWidthCellRendererParams,
    onGridReady = () => {},
    onRowDataChanged = () => {},
    onSelectionChanged = () => {},
    getRowStyle = () => {},
    onRowClicked = () => {},
    onCellClicked = () => {},
    onCellValueChanged = () => {},
    onCellEditingStopped = () => {},
    onCellFocused = () => {},
    onColumnMoved = () => {},
    onColumnResized = () => {},
    onFilterChanged = () => {},
    getRowClass = () => {},
    rowClassRules = () => {},
    suppressDragLeaveHidesColumns = true,
  } = props;
  const [api, setApi] = useState(null);
  const [columnApi, setColumnApi] = useState(null);
  const gridRef = useRef(null);
  const gridReady = (params) => {
    setApi(params.api);
    setColumnApi(params.columnApi);
    onGridReady(params);
  };
  useImperativeHandle(ref, () => {
    return {
      ...gridRef.current,
      isFocused: () => {
        return !!api?.getFocusedCell();
      },
      focus: (focusLastRow = false, column = null) => {
        if (api?.getDisplayedRowCount() > 0) {
          setTimeout(() => {
            let currentRowIndex = null;
            let currentColumn = null;
            if (api.getFocusedCell()) {
              currentRowIndex = api.getFocusedCell().rowIndex;
            }
            if (!currentRowIndex) {
              if (focusLastRow) {
                currentRowIndex = api.getDisplayedRowCount() - 1;
              } else {
                currentRowIndex = 0;
              }
            }

            if (column) {
              currentColumn = columnApi.getColumn(column);
            } else {
              if (api.getFocusedCell()?.column?.getColDef()?.editable) {
                currentColumn = api.getFocusedCell().column;
              } else {
                let hasEditableColumn = false;
                let temp = null;
                let index = 0;
                while (
                  !hasEditableColumn &&
                  index !== columnApi.getAllDisplayedColumnGroups().length - 1
                ) {
                  hasEditableColumn = columnApi
                    .getAllDisplayedColumnGroups()
                    [index].getColDef().editable;
                  temp = columnApi.getAllDisplayedColumnGroups()[index];
                  index++;
                }
                if (hasEditableColumn && temp) {
                  currentColumn = temp;
                }
              }
            }
            if (!currentColumn) {
              currentColumn = columnApi.getAllDisplayedColumns()[0];
            }
            focusCell(api, currentRowIndex, currentColumn);
          }, 1);
          return;
        }
      },
      blur: () => {
        api?.deselectAll();
        api?.clearFocusedCell();
      },
    };
  });
  return (
    <div style={{ height: "100%", width: "100%" }}>
      <div
        className="ag-theme-balham"
        id={id}
        onFocus={() => {
          ref?.current?.focus();
        }}
        tabIndex={1}
        style={{ width: "100%", height: "100%" }}
      >
        <AgGridReact
          reactUi={true}
          id={id}
          ref={gridRef}
          rowSelection={rowSelection}
          rowData={rowData}
          singleClickEdit={singleClickEdit}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          stopEditingWhenCellsLoseFocus={stopEditingWhenCellsLoseFocus}
          suppressHorizontalScroll={suppressHorizontalScroll}
          suppressCellSelection={suppressCellSelection}
          frameworkComponents={frameworkComponents}
          quickFilterText={quickFilterText}
          animateRows={animateRows}
          onGridReady={(params) => gridReady(params)}
          onRowDataChanged={onRowDataChanged}
          onSelectionChanged={(params) => {
            params.api.resetRowHeights();
            onSelectionChanged(params);
          }}
          getRowStyle={getRowStyle}
          onCellClicked={onCellClicked}
          onCellValueChanged={onCellValueChanged}
          onCellEditingStopped={onCellEditingStopped}
          onCellFocused={(params) => {
            if (params.rowIndex !== null && params.column !== null)
              onCellFocused(params);
          }}
          onColumnMoved={onColumnMoved}
          onColumnResized={onColumnResized}
          getRowHeight={(params) => (params.node.selected ? 40 : 30)}
          isFullWidthCell={isFullWidthCell}
          fullWidthCellRenderer={fullWidthCellRenderer}
          fullWidthCellRendererFramework={fullWidthCellRendererFramework}
          fullWidthCellRendererParams={fullWidthCellRendererParams}
          onFilterChanged={onFilterChanged}
          onRowClicked={onRowClicked}
          rowClassRules={rowClassRules}
          getRowClass={getRowClass}
          suppressDragLeaveHidesColumns={suppressDragLeaveHidesColumns}
        />
      </div>
    </div>
  );
});

export default Aggrid;
